/* ============================================================
   <cart-drawer> : panier latéral flottant.
   Interface publique : renderContents({ sections }), appelée par
   le JS d'ajout au panier des sections produit avec les sections
   'cart-drawer' et 'cart-icon-bubble' rendues par Shopify.
   Gère aussi : +/- et suppression de ligne via /cart/change.js,
   l'ouverture depuis l'icône panier du header (href = /cart en
   repli sans JS), le focus (dialog modal) et les annonces
   lecteur d'écran via la région persistante #CartDrawer-Live.
   ============================================================ */

class CartDrawer extends HTMLElement {
  connectedCallback() {
    if (this.__init) return;
    this.__init = true;
    this.__lastActive = null;

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-drawer-close]')) {
        this.close();
        return;
      }
      const qtyButton = event.target.closest('[data-qty-change]');
      if (qtyButton) this.changeLine(qtyButton);
    });

    // L'icône panier du header ouvre le drawer au lieu de naviguer vers /cart.
    // Clic modifié (ctrl/cmd/maj/alt) : navigation native conservée (A-08).
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('#cart-icon-bubble');
      if (!trigger) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.open();
    });

    document.addEventListener('keydown', (event) => {
      if (!this.classList.contains('is-open')) return;
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      if (event.key === 'Tab') this.trapFocus(event);
    });
  }

  open() {
    if (!this.classList.contains('is-open')) this.__lastActive = document.activeElement;
    const panel = this.querySelector('.cart-drawer__panel');
    if (panel) {
      panel.inert = false;
      panel.removeAttribute('aria-hidden');
      panel.setAttribute('aria-modal', 'true');
    }
    this.classList.add('is-open');
    document.body.classList.add('cart-drawer-open');
    if (window.__teaboLenis) window.__teaboLenis.stop();
    const closeButton = this.querySelector('button[data-cart-drawer-close]');
    if (closeButton) closeButton.focus();
    else if (panel) panel.focus();
  }

  close() {
    const wasOpen = this.classList.contains('is-open');
    this.classList.remove('is-open');
    document.body.classList.remove('cart-drawer-open');
    if (window.__teaboLenis) window.__teaboLenis.start();
    // Restaure le focus sur l'élément déclencheur (J-05).
    if (wasOpen && this.__lastActive && document.contains(this.__lastActive)) this.__lastActive.focus();
    const panel = this.querySelector('.cart-drawer__panel');
    if (panel) {
      panel.inert = true;
      panel.setAttribute('aria-hidden', 'true');
      panel.removeAttribute('aria-modal');
    }
    this.__lastActive = null;
  }

  /* Piège Tab dans le panneau : role=dialog + aria-modal (J-05). */
  trapFocus(event) {
    const focusables = Array.from(this.querySelectorAll(
      '.cart-drawer__panel a[href], .cart-drawer__panel button:not([disabled]), .cart-drawer__panel input:not([disabled]), .cart-drawer__panel [tabindex]:not([tabindex="-1"])'
    )).filter((element) => element.getClientRects().length && !element.closest('[hidden], [inert]'));
    if (!focusables.length) return;
    // Safari peut ignorer des liens/boutons dans son parcours Tab natif.
    // Piloter chaque pas garantit le même cycle dans tous les navigateurs.
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    const index = focusables.indexOf(document.activeElement);
    const next = index < 0
      ? (event.shiftKey ? focusables.length - 1 : 0)
      : (index + (event.shiftKey ? -1 : 1) + focusables.length) % focusables.length;
    event.preventDefault();
    focusables[next].focus();
  }

  /* Remplace le contenu du drawer et la bulle du header à partir
     des sections rendues, annonce l'état, puis ouvre le drawer. */
  renderContents(parsedState) {
    const sections = parsedState.sections || {};
    if (sections['cart-drawer']) {
      const contents = this.querySelector('#CartDrawer-Contents');
      if (contents) contents.innerHTML = this.constructor.extractSection(sections['cart-drawer']);
    }
    if (sections['cart-icon-bubble']) {
      const bubble = document.getElementById('cart-icon-bubble');
      const html = this.constructor.extractSection(sections['cart-icon-bubble']);
      if (bubble?.matches('.v5hd__panier')) {
        // Garder l'icône et le libellé v5 : la section technique utilise le balisage legacy.
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const count = Number(doc.querySelector('[data-cart-count]')?.dataset.cartCount || 0);
        let badge = bubble.querySelector('.v5hd__pastille');
        if (count > 0) {
          if (!badge) {
            badge = document.createElement('span');
            badge.className = 'v5hd__pastille';
            bubble.appendChild(badge);
          }
          badge.textContent = String(count);
        } else if (badge) badge.remove();
        bubble.setAttribute('aria-label', 'Panier, ' + count + ' article' + (count > 1 ? 's' : ''));
        const description = bubble.querySelector('.visually-hidden');
        if (description) description.textContent = count + ' article(s) dans le panier';
      } else if (bubble) bubble.innerHTML = html;
    }
    this.announce();
    this.open();
  }

  // Une relecture ne modifie pas le panier, même après une réponse d'ajout perdue.
  async refresh() {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(root + 'cart?sections=cart-drawer,cart-icon-bubble', {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Panier indisponible');
    const sections = await response.json();
    if (!sections['cart-drawer']) throw new Error('Panier indisponible');
    this.renderContents({ sections });
  }

  /* Annonce le nouvel état dans la région aria-live persistante (J-06). */
  announce() {
    const live = document.getElementById('CartDrawer-Live');
    const head = this.querySelector('.cart-drawer__head');
    if (!live || !head) return;
    const count = Number(head.dataset.itemCount || 0);
    live.textContent = count > 0
      ? 'Panier mis à jour : ' + count + ' article' + (count > 1 ? 's' : '') + '.'
      : 'Votre panier est vide.';
  }

  /* Message d'erreur discret sous l'entête ('' = masqué).
     Le texte est posé après l'affichage : une région masquée n'est pas annoncée. */
  showError(message) {
    const zone = this.querySelector('[data-cart-drawer-error]');
    if (!zone) return;
    if (!message) {
      zone.hidden = true;
      zone.textContent = '';
      return;
    }
    zone.hidden = false;
    zone.textContent = message;
  }

  static extractSection(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const section = doc.querySelector('.shopify-section');
    return section ? section.innerHTML : html;
  }

  async changeLine(button) {
    const line = Number(button.dataset.line);
    const change = button.dataset.qtyChange;
    const row = button.closest('.cart-drawer__item');
    const currentQty = row ? Number(row.querySelector('.cart-drawer__qty span').textContent) : 1;
    const quantity = change === 'remove' ? 0 : Math.max(0, currentQty + Number(change));

    button.disabled = true;
    this.showError('');
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity, sections: 'cart-drawer,cart-icon-bubble' }),
      });
      const data = await response.json();
      if (!response.ok || !data.sections) {
        // Réponse d'erreur (422...) : message affiché, bouton réactivé au finally (A-07).
        let message = '';
        if (typeof data.errors === 'string') message = data.errors;
        this.showError(message || data.description || data.message || 'Mise à jour impossible pour le moment.');
        return;
      }
      const wasOpen = this.classList.contains('is-open');
      this.renderContents({ sections: data.sections });
      if (!wasOpen) {
        this.close();
        return;
      }
      // Le re-rendu détruit le bouton porteur du focus : on revient sur
      // son équivalent, sinon un bouton de quantité, sinon Fermer (J-06).
      const next =
        this.querySelector('[data-qty-change="' + change + '"][data-line="' + line + '"]') ||
        this.querySelector('.cart-drawer__items [data-qty-change]') ||
        this.querySelector('.cart-drawer__close');
      if (next) next.focus();
    } catch (error) {
      this.showError('Mise à jour impossible pour le moment. Vérifiez votre connexion.');
    } finally {
      // Sans effet après un re-rendu réussi : le bouton n'est plus dans le DOM.
      button.disabled = false;
    }
  }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);
